import React from "react";
import { AlertTriangle, CheckCircle, Clipboard } from "react-feather";
import SectionCard from "../../../components/common/SectionCard";

// The technician's own service report, READ-ONLY.
//
// Entering it is a field activity (the readings are taken on site and the
// "signature" is the server-resolved identity of whoever saved it), so the web
// module deliberately does not offer the form even though the endpoint exists.
// Reading it is the whole point of the web module: verifying what was reported
// before closing the job is exactly what a manager logs in to do.
//
// TimeIn is set ONLY by a QR check-in on site; TimeOut by Hold/Complete. A
// report with no TimeIn means site presence was never verified - worth
// flagging to a verifier rather than leaving as a silent blank.

// SQL TIME serialises as "17:14:39.6701021" - rendering it raw was a real bug
// on the Flutter screen. Trim to HH:MM.
export const formatClockTime = (value) => {
  if (!value) return null;
  const match = String(value).match(/^(\d{1,2}):(\d{2})/);
  return match ? `${match[1].padStart(2, "0")}:${match[2]}` : String(value);
};

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "-";

const ServiceReportPanel = ({ report, items = [], sourceType }) => {
  const wasSaved = Boolean(report?.recordGuid);
  const timeIn = formatClockTime(report?.timeIn);
  const timeOut = formatClockTime(report?.timeOut);

  // Checklist items only exist for the branch's configured list; a branch with
  // none configured legitimately returns an empty array, which is not the same
  // thing as a technician skipping them.
  const filled = items.filter((i) => i.itemValue != null && String(i.itemValue).trim() !== "");
  const hasChecklist = items.length > 0;

  return (
    <SectionCard
      icon={Clipboard}
      title="Service Report"
      right={
        hasChecklist && (
          <span className={`badge ${filled.length === items.length ? "badge-linesuccess" : "badge-linewarning"}`}>
            {filled.length}/{items.length} checklist items
          </span>
        )
      }
    >
      <>
        {!wasSaved ? (
          <p className="text-muted mb-0">
            The technician has not saved a service report for this job yet.
            {sourceType === "SERVICE_VISIT" && " A service visit cannot be completed until every checklist item has a value."}
          </p>
        ) : (
          <>
            <div className="row mb-3">
              <div className="col-md-3">
                <small className="text-muted d-block">Time in</small>
                <span>{timeIn || <span className="text-muted">not checked in</span>}</span>
              </div>
              <div className="col-md-3">
                <small className="text-muted d-block">Time out</small>
                <span>{timeOut || <span className="text-muted">-</span>}</span>
              </div>
              <div className="col-md-6">
                <small className="text-muted d-block">Signed by</small>
                <span>
                  {report.reportedByName || "-"}
                  {report.reportedByDesignation ? ` · ${report.reportedByDesignation}` : ""}
                  {report.reportedDate ? ` · ${formatDate(report.reportedDate)}` : ""}
                </span>
              </div>
            </div>

            {!timeIn && (
              <div className="alert alert-warning py-2 px-3 d-flex align-items-center">
                <AlertTriangle size={15} className="me-2 flex-shrink-0" />
                <small className="mb-0">No QR check-in &mdash; site presence was never verified.</small>
              </div>
            )}

            {report.techComment && (
              <div className="mb-3">
                <small className="text-muted d-block">Technician's comment</small>
                <div>{report.techComment}</div>
              </div>
            )}

            {report.customerComment && (
              <div className="mb-3">
                <small className="text-muted d-block">
                  Customer's comment
                  {report.customerCommentByName ? ` · recorded by ${report.customerCommentByName}` : ""}
                </small>
                <div>{report.customerComment}</div>
              </div>
            )}
          </>
        )}

        {hasChecklist && (
          <div className="table-responsive mt-2">
            <table className="table table-sm mb-0">
              <thead>
                <tr>
                  <th style={{ width: "60%" }}>Checklist item</th>
                  <th>Reading</th>
                </tr>
              </thead>
              <tbody>
                {[...items]
                  .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                  .map((item) => {
                    const value = item.itemValue != null && String(item.itemValue).trim() !== "" ? item.itemValue : null;
                    return (
                      <tr key={item.checklistItemId}>
                        <td>{item.itemName}</td>
                        <td>
                          {value ? (
                            <span className="d-inline-flex align-items-center">
                              <CheckCircle size={13} className="text-success me-1" />
                              {value}
                            </span>
                          ) : (
                            <span className="text-muted fst-italic">not recorded</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
      </>
    </SectionCard>
  );
};

export default ServiceReportPanel;
